/**
 * Media layout utilities for Dashboard feed and Memory Detail.
 *
 * - focalPosition: compute object-position for cropped contexts
 * - getMosaicTemplate: pick feed card layout by photo count
 */

/**
 * Compute object-position for a cropped image tile.
 * Biases toward faces (which sit high in portraits).
 *
 * @param {{ fx?: number, fy?: number, w?: number, h?: number }} media
 * @returns {string} CSS object-position value
 */
export function focalPosition(media) {
  if (media.fx != null && media.fy != null) {
    return `${media.fx * 100}% ${media.fy * 100}%`;
  }
  const w = media.w || media.naturalWidth || 1;
  const h = media.h || media.naturalHeight || 1;
  const ar = w / h;
  // Portraits: faces sit high → bias upward
  return ar < 0.9 ? '50% 28%' : '50% 45%';
}

/**
 * Get the mosaic template for a feed card based on photo count.
 *
 * @param {number} count - number of photos
 * @returns {{ type: string, visibleCount: number, remainder: number }}
 */
export function getMosaicTemplate(count) {
  if (count <= 0) return { type: 'none', visibleCount: 0, remainder: 0 };
  if (count === 1) return { type: 'single', visibleCount: 1, remainder: 0 };
  if (count === 2) return { type: 'duo', visibleCount: 2, remainder: 0 };
  if (count === 3) return { type: 'trio', visibleCount: 3, remainder: 0 };
  return { type: 'quad', visibleCount: 3, remainder: count - 3 };
}

/**
 * Category badge config.
 */
export const CATEGORY_CONFIG = {
  family_memory: { label: 'Family Memory', bg: '#F0F5E8', fg: '#556B2F', icon: 'BookOpen' },
  celebration: { label: 'Celebration', bg: '#FFF0E0', fg: '#C65D00', icon: 'Trophy' },
  funny_moment: { label: 'Funny Moment', bg: '#FFF0E0', fg: '#C65D00', icon: 'Trophy' },
  wisdom: { label: 'Wisdom', bg: '#FCEFD8', fg: '#B5760A', icon: 'Lightbulb' },
  life_lesson: { label: 'Life Lesson', bg: '#FCEFD8', fg: '#B5760A', icon: 'Lightbulb' },
  milestone: { label: 'Milestone', bg: '#FFF7E0', fg: '#B8860B', icon: 'Trophy' },
  capsule: { label: 'Time Capsule', bg: '#E8F0FE', fg: '#3B72C4', icon: 'Mail' },
  personal_experience: { label: 'Personal', bg: '#F0E8FF', fg: '#6B3FA0', icon: 'Feather' },
  historical_event: { label: 'Historical', bg: '#F0F5E8', fg: '#556B2F', icon: 'Clock' },
};

export function getCategoryConfig(category) {
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.family_memory;
}

/**
 * Group stories into time buckets for the feed.
 *
 * @param {Array} stories - sorted newest first
 * @returns {Array<{ label: string, stories: Array }>}
 */
export function groupByTimeBucket(stories) {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const buckets = {
    thisWeek: { label: 'This Week', stories: [] },
    thisMonth: { label: 'Earlier this month', stories: [] },
    earlier: { label: 'Earlier', stories: [] },
  };

  stories.forEach(story => {
    const date = new Date(story.date || story.created_at || story.sharedAt);
    if (date >= startOfWeek) {
      buckets.thisWeek.stories.push(story);
    } else if (date >= startOfMonth) {
      buckets.thisMonth.stories.push(story);
    } else {
      buckets.earlier.stories.push(story);
    }
  });

  return Object.values(buckets).filter(b => b.stories.length > 0);
}
